/**
 * FinderJigsawPage — client-side jigsaw picture puzzle.
 *
 * Slices a real Aussie photo (served from cdn.hustleplate.au) into an
 * N×N grid, shuffles the pieces into the tray below, and lets the
 * player drag pieces onto the board. Snaps within 24 px of the
 * correct slot.
 *
 * Images live in the ``hustleplate-assets`` R2 bucket under
 * ``finder/jigsaw/{key}.jpg`` — public URLs are served through the
 * cdn.hustleplate.au custom domain (long-cache, immutable). Generated
 * via ``backend/scripts/gen_jigsaw_scenes.py``.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";

const CDN_BASE = "https://cdn.hustleplate.au/finder/jigsaw";

const SCENES = [
  { key: "koala",    title: "Koala on gum",       hue: "#e6f4ea" },
  { key: "kangaroo", title: "Kangaroo Outback",   hue: "#fef3e7" },
  { key: "opera",    title: "Sydney Opera House", hue: "#e6f2fb" },
  { key: "reef",     title: "Great Barrier Reef", hue: "#e0f4ff" },
  { key: "uluru",    title: "Uluru Sunset",       hue: "#fff0e2" },
  { key: "bondi",    title: "Bondi Beach",        hue: "#e0f4ff" },
].map((s) => ({ ...s, imgUrl: `${CDN_BASE}/${s.key}.jpg` }));

const DIFFICULTIES = [
  { key: "easy",   label: "Easy",   n: 3 },
  { key: "medium", label: "Medium", n: 5 },
  { key: "hard",   label: "Hard",   n: 7 },
];

const BOARD_SIZE = 420; // px on desktop; scales down via viewport
const SNAP_PX = 28;

function shuffled(n) {
  const arr = Array.from({ length: n }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function FinderJigsawPage() {
  const [sceneKey, setSceneKey] = useState(SCENES[0].key);
  const [diffKey, setDiffKey]   = useState("medium");
  const [showRef, setShowRef]   = useState(false);
  const [placed, setPlaced]     = useState(() => ({})); // {pieceIdx: slotIdx}
  const [tray, setTray]         = useState([]);
  const [dragPiece, setDragPiece] = useState(null); // {idx, offX, offY, x, y}
  const [imgReady, setImgReady] = useState(false);
  const boardRef = useRef(null);

  const scene = useMemo(
    () => SCENES.find((s) => s.key === sceneKey) || SCENES[0],
    [sceneKey],
  );
  const diff  = DIFFICULTIES.find((d) => d.key === diffKey) || DIFFICULTIES[1];
  const N = diff.n;
  const total = N * N;
  const pieceSize = BOARD_SIZE / N;

  useEffect(() => {
    document.title = "Jigsaw — HustleFinder Kids";
  }, []);

  // Preload the current scene image so the tray + board render
  // immediately at final quality (no flicker mid-drag).
  useEffect(() => {
    setImgReady(false);
    const im = new Image();
    im.onload  = () => setImgReady(true);
    im.onerror = () => setImgReady(true); // still let user attempt
    im.src = scene.imgUrl;
  }, [scene.imgUrl]);

  // Reset when scene or difficulty changes.
  useEffect(() => {
    setPlaced({});
    setTray(shuffled(total));
    setDragPiece(null);
  }, [sceneKey, diffKey, total]);

  const solved = Object.keys(placed).length === total &&
                 Object.entries(placed).every(([p, s]) => Number(p) === s);

  const beginDrag = (idx, e) => {
    e.preventDefault();
    const t = e.touches ? e.touches[0] : e;
    setDragPiece({
      idx,
      x: t.clientX,
      y: t.clientY,
      offX: pieceSize / 2,
      offY: pieceSize / 2,
    });
  };

  const onMove = useCallback((e) => {
    if (!dragPiece) return;
    const t = e.touches ? e.touches[0] : e;
    setDragPiece((p) => (p ? { ...p, x: t.clientX, y: t.clientY } : null));
  }, [dragPiece]);

  const onDrop = useCallback(() => {
    if (!dragPiece) return;
    const board = boardRef.current;
    if (!board) { setDragPiece(null); return; }
    const rect = board.getBoundingClientRect();
    const relX = dragPiece.x - rect.left - dragPiece.offX;
    const relY = dragPiece.y - rect.top  - dragPiece.offY;
    if (relX < -SNAP_PX || relY < -SNAP_PX ||
        relX > rect.width + SNAP_PX || relY > rect.height + SNAP_PX) {
      // dropped outside — send back to tray
      setDragPiece(null);
      return;
    }
    // find nearest slot centre
    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < total; i++) {
      const sr = Math.floor(i / N);
      const sc = i % N;
      const cx = sc * pieceSize;
      const cy = sr * pieceSize;
      const dist = Math.hypot(relX - cx, relY - cy);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    if (best === -1 || bestDist > SNAP_PX + pieceSize / 2) {
      setDragPiece(null);
      return;
    }
    // if slot is occupied, swap the piece back to tray
    setPlaced((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (next[k] === best && Number(k) !== dragPiece.idx) {
          delete next[k];
          setTray((tr) => tr.includes(Number(k)) ? tr : [...tr, Number(k)]);
        }
      }
      delete next[dragPiece.idx];
      next[dragPiece.idx] = best;
      return next;
    });
    setTray((tr) => tr.filter((p) => p !== dragPiece.idx));
    setDragPiece(null);
  }, [dragPiece, total, N, pieceSize]);

  useEffect(() => {
    if (!dragPiece) return;
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", onDrop);
    window.addEventListener("touchend", onDrop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onDrop);
      window.removeEventListener("touchend", onDrop);
    };
  }, [dragPiece, onMove, onDrop]);

  const pieceStyle = (idx) => {
    const sr = Math.floor(idx / N);
    const sc = idx % N;
    return {
      backgroundImage: `url("${scene.imgUrl}")`,
      backgroundSize: `${BOARD_SIZE}px ${BOARD_SIZE}px`,
      backgroundPosition: `-${sc * pieceSize}px -${sr * pieceSize}px`,
      width:  `${pieceSize}px`,
      height: `${pieceSize}px`,
    };
  };

  const shuffleAgain = () => {
    setPlaced({});
    setTray(shuffled(total));
  };

  return (
    <div
      data-testid="jigsaw-page"
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-24"
    >
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Link
          to="/finder/kids"
          data-testid="jigsaw-back-link"
          className="text-xs text-emerald-700 hover:underline"
        >
          ← back to Kid Zone
        </Link>
        <h1 className="text-3xl font-bold text-slate-900 mt-3 mb-1">
          Jigsaw
        </h1>
        <p className="text-sm text-slate-500 mb-4">
          Drag the pieces onto the board. Snap into place when close enough.
        </p>

        {solved && (
          <div
            data-testid="jigsaw-won-banner"
            className="bg-emerald-100 border border-emerald-300 text-emerald-800 rounded-xl p-3 mb-3 text-sm font-semibold text-center"
          >
            Puzzle solved!
          </div>
        )}

        {/* Scene picker */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-3" data-testid="jigsaw-scenes">
          {SCENES.map((s) => (
            <button
              key={s.key}
              onClick={() => setSceneKey(s.key)}
              data-testid={`jigsaw-scene-${s.key}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs border transition ${
                sceneKey === s.key
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-slate-700 border-slate-300 hover:border-emerald-500"
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>

        {/* Difficulty */}
        <div className="flex gap-2 mb-4" data-testid="jigsaw-difficulty">
          {DIFFICULTIES.map((d) => (
            <button
              key={d.key}
              onClick={() => setDiffKey(d.key)}
              data-testid={`jigsaw-diff-${d.key}`}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold ${
                diffKey === d.key
                  ? "bg-emerald-600 text-white"
                  : "bg-white border border-slate-200 hover:border-emerald-400"
              }`}
            >
              {d.label} <span className="text-[10px] opacity-75">{d.n}×{d.n}</span>
            </button>
          ))}
        </div>

        {/* Board */}
        <div className="relative bg-white border-2 border-slate-300 rounded-2xl overflow-hidden mx-auto"
             style={{
               width: `${BOARD_SIZE}px`,
               height: `${BOARD_SIZE}px`,
               maxWidth: "100%",
               backgroundColor: scene.hue,
             }}
             ref={boardRef}
             data-testid="jigsaw-board"
        >
          {/* Reference underlay */}
          {showRef && (
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                backgroundImage: `url("${scene.imgUrl}")`,
                backgroundSize: "100% 100%",
              }}
            />
          )}
          {/* Placed pieces */}
          {Object.entries(placed).map(([pieceIdx, slotIdx]) => {
            const p = Number(pieceIdx);
            const s = Number(slotIdx);
            const sr = Math.floor(s / N);
            const sc = s % N;
            const correct = p === s;
            return (
              <div
                key={`placed-${p}`}
                onMouseDown={(e) => beginDrag(p, e)}
                onTouchStart={(e) => beginDrag(p, e)}
                data-testid={`jigsaw-piece-placed-${p}`}
                className={`absolute cursor-grab active:cursor-grabbing ${
                  correct ? "" : "ring-2 ring-red-400"
                }`}
                style={{
                  ...pieceStyle(p),
                  left:   `${sc * pieceSize}px`,
                  top:    `${sr * pieceSize}px`,
                  border: "1px solid rgba(0,0,0,0.15)",
                }}
              />
            );
          })}
          {!imgReady && (
            <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
              Loading photo…
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex gap-2 mt-4" data-testid="jigsaw-controls">
          <button
            onClick={() => setShowRef((s) => !s)}
            data-testid="jigsaw-ref-btn"
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold ${
              showRef
                ? "bg-slate-900 text-white"
                : "bg-white border border-slate-200 hover:border-emerald-400"
            }`}
          >
            {showRef ? "Hide reference" : "Show reference"}
          </button>
          <button
            onClick={shuffleAgain}
            data-testid="jigsaw-shuffle-btn"
            className="py-2 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
          >
            Shuffle
          </button>
        </div>

        {/* Tray */}
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mt-6 mb-2">
          Pieces
        </h3>
        <div
          className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-2 min-h-[80px]"
          data-testid="jigsaw-tray"
        >
          {tray.map((p) => (
            <div
              key={`tray-${p}`}
              onMouseDown={(e) => beginDrag(p, e)}
              onTouchStart={(e) => beginDrag(p, e)}
              data-testid={`jigsaw-piece-tray-${p}`}
              className="cursor-grab active:cursor-grabbing border border-slate-300 bg-white"
              style={pieceStyle(p)}
            />
          ))}
        </div>
      </div>

      {/* Floating drag ghost */}
      {dragPiece && (
        <div
          className="fixed pointer-events-none z-50 border border-emerald-500 shadow-lg"
          style={{
            ...pieceStyle(dragPiece.idx),
            left: dragPiece.x - dragPiece.offX,
            top:  dragPiece.y - dragPiece.offY,
          }}
        />
      )}
    </div>
  );
}
